import {
  Button,
  Checkbox,
  FormRow,
  Input,
  Modal,
  Radio,
  Spinner,
  Stack,
  Text
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  ApiRoutes,
  mandantClient,
  userConflictErrorMsg
} from '../api/mandant-api';
import {
  AvailableRolesResponse,
  KeycloakAssignedGroups,
  KeycloakGroup,
  UserInviteConfig,
  UserRequestDto
} from '../api/mandant.types';
import { useNotification } from '../shared/utils/notification/useNotification';
import { ErrorType } from '../shared/error/ErrorCode';
import { getMandant } from '../shared/utils/auth/mandant';

const SYSTEM_GROUP_CONSTANT: string = 'Administration';

interface CreateAndUpdateMandantCredsModalProps {
  readonly onClose: () => void;
  readonly isOpen: boolean;
}

export function UserAndRoleManagementModal({
  onClose
}: CreateAndUpdateMandantCredsModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUser, notifyUserFromRequest } = useNotification();

  const [selectedKeycloakGroups, setSelectedKeycloakGroups] = useState<
    Array<KeycloakAssignedGroups>
  >([]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid }
  } = useForm<{ email: string; groups?: string }>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const {data: roles}: {data?: AvailableRolesResponse} = useQuery<AvailableRolesResponse>({
    queryKey: [ApiRoutes.availableRoles()]
  })

  const { mutateAsync: inviteUser, isPending: isInviting } = useMutation({
    mutationFn: (userRequest: UserRequestDto) =>
      notifyUserFromRequest(
        () => mandantClient.post(ApiRoutes.usersInvite(), userRequest),
        {
          successMessage: t('api.success.inviteUser'),
          errorMessage: t('api.error.inviteUser'),
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_USER_CONFLICT]: () => {
              notifyUser({ intent: 'error', title: t('api.error.inviteUser.conflict') });
            }
          }
        }
      ),
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries({
        queryKey: [ApiRoutes.users()]
      });
    }
  });

  const onSubmit = async ({ email, groups }: { email: string; groups: string }) => {
    await inviteUser({
      email,
      orgUnitRoles: [],
      systemRoles: groups ? [groups] : [],
      redirectUri: `${window.location.origin}/${getMandant()}`
    } as UserRequestDto).catch(() => {});
  };

  function areSelectedRolesValid(groups: Array<KeycloakAssignedGroups>) {
    const hasSelectedAdminRole = groups.some(
      (k) => k.groupName === SYSTEM_GROUP_CONSTANT
    );
    const hasNotSelectedSubGroupName = groups.some(
      (k) =>
        k.groupName &&
        k.groupName !== SYSTEM_GROUP_CONSTANT &&
        k.subGroups.length === 0
    );
    if (hasNotSelectedSubGroupName && groups.length > 0) {
      return false;
    }
    if (hasSelectedAdminRole) {
      return true;
    }
    return groups.length >= 0;
  }

  const checkedRadio = (groupName: string, groupIds: Array<string>) => {
    const assign = selectedKeycloakGroups.find(
      (value) => value.groupName === groupName
    );

    if (!assign) {
      return false;
    }

    if (assign.subGroups.length !== groupIds.length) {
      return false;
    }

    let correct = true;

    for (const subgroup of assign.subGroups) {
      let innerCorrect = false;

      if (groupIds.some((id) => id === subgroup.id)) {
        innerCorrect = true;
      }

      correct = correct && innerCorrect;
    }
    return correct;
  };

  const checkRadioDisabled = (groupName: string) => {
    return (
      selectedKeycloakGroups.find((value) => value.groupName === groupName) ===
      undefined
    );
  };

  const onRadioChanged = (
    checked: boolean,
    groupName: string,
    subgroups: Array<KeycloakGroup>
  ) => {
    if (checked) {
      setSelectedKeycloakGroups((prev) => {
        const next = prev.map((value) =>
          value.groupName === groupName
            ? ({ groupName: value.groupName, subGroups: subgroups } as KeycloakAssignedGroups)
            : value
        );
        updateGroupsField(next);
        return next;
      });
    }
  };

  const onSystemRoleChange = (checked: boolean, groupName: string) => {
    setSelectedKeycloakGroups((prev) => {
      const next = checked
        ? [...prev, { groupName, subGroups: [] } as KeycloakAssignedGroups]
        : prev.filter((value) => value.groupName !== groupName);
      updateGroupsField(next);
      return next;
    });
  };

  const updateGroupsField = (next: Array<KeycloakAssignedGroups>) => {
    setValue(
      'groups',
      next.at(0)?.groupName,
      { shouldValidate: true }
    );
  };

  const onChangeCheckbox = (checked: boolean, groupName: string) => {
    setSelectedKeycloakGroups((prev) => {
      const next = checked
        ? [...prev, { groupName, subGroups: [] } as KeycloakAssignedGroups]
        : prev.filter((value) => value.groupName !== groupName);
      updateGroupsField(next);
      return next;
    });
  };
  return (
    <Modal open onClose={onClose}>
      <Modal.Header id="modal-editUserRoles-header">
        {t('userAndroleManagementModal.headline')}
      </Modal.Header>

      <Modal.Content>
        <form
          id="inviteUserForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Stack spacing="l">
            <input type="hidden" {...register('groups')} />
            <FormRow
              label={`${t('userAndroleManagementModal.emailInput')} *`}
              validationMessage={errors.email?.message}
              variant={errors.email ? 'error' : undefined}
            >
              <Input
                id="emailToRegister"
                type="text"
                placeholder="example@abc.com"
                disabled={isInviting}
                {...register('email', {
                  required: t('userAndroleManagementModal.validation.emailRequired'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('userAndroleManagementModal.validation.emailInvalid')
                  }
                })}
              />
            </FormRow>
            <FormRow
              label={t('userAndroleManagementModal.adminLabel')}
              tooltip={t('userAndroleManagementModal.adminTooltip')}
            >
              <Stack id="systemGroupStack" direction="column">
                {roles?.systemRoles?.map((value) => (
                  <Checkbox
                    label={value}
                    id={`rolle_${value}`}
                    key={`key_rolle_${value}`}
                    onChange={(event) =>
                      onSystemRoleChange(
                        event.currentTarget.checked,
                        value
                      )
                    }
                  />
                ))}
              </Stack>
            </FormRow>
          </Stack>
        </form>
      </Modal.Content>
      <Modal.Footer direction="rtl" alignment="none">
        <Button variant="secondary-outline" onClick={onClose}>
          {t('globalNotifications.modalAboard')}
        </Button>
        <Button
          id="submitButton"
          type="submit"
          form="inviteUserForm"
          variant="primary"
          disabled={!isValid || isInviting}
        >
          {isInviting ? <Spinner size="sm" /> : t('userAndroleManagementModal.submitButton')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
